import { useCallback, useEffect, useState } from "react";
import {
  RISK_FACTOR_OPTIONS,
  RISK_UI_FIXED_LOT,
  defaultRiskValueForType,
} from "constants/copyTradeRisk";
import { apiUrl } from "utils/apiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";
import { FIRST_MONTH_RISK_NOTICE } from "constants/firstMonthRiskMessage";
import { useFirstMonthRiskLocks } from "hooks/useFirstMonthRiskLocks";
import { isSlotRiskLocked } from "utils/riskLocks";
import { pushCopySettingsToDuplikium } from "utils/copySlaveSettings";

const TH =
  "border-b border-gray-200 bg-gray-100/80 px-2 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:border-white/10 dark:bg-navy-900/80 dark:text-gray-300";
const TD =
  "border-b border-gray-100 px-2 py-2 align-middle text-center text-sm font-semibold text-navy-800 dark:border-white/5 dark:text-gray-100";
const SELECT_CLASS =
  "mx-auto w-full max-w-[160px] rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm font-semibold text-navy-800 outline-none focus:border-brand-400 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:bg-navy-900 dark:text-white";
const INPUT_CLASS =
  "mx-auto w-full max-w-[88px] rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm font-semibold tabular-nums text-navy-800 outline-none focus:border-brand-400 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:bg-navy-900 dark:text-white";

function defaultRiskCfg() {
  return {
    riskManagement: RISK_UI_FIXED_LOT,
    riskValue: "0.01",
    copyOn: true,
  };
}

function riskRowFromServer(slotCfg, { locked = false } = {}) {
  if (locked) return defaultRiskCfg();
  if (!slotCfg) return defaultRiskCfg();
  return {
    riskManagement: slotCfg.riskManagement || RISK_UI_FIXED_LOT,
    riskValue: String(slotCfg.riskValue ?? "0.01"),
    copyOn: slotCfg.copyOn !== false,
  };
}

function riskRowsEqual(a, b) {
  return (
    a.riskManagement === b.riskManagement &&
    String(a.riskValue) === String(b.riskValue)
  );
}

function StatusCell({ row, perf }) {
  const maxDd =
    perf?.maxDrawdownPercent != null
      ? `${perf.maxDrawdownPercent}%`
      : null;

  if (row.subscriptionExpired) {
    return (
      <div className="flex flex-col items-center gap-0.5 text-center">
        <span className="text-sm font-bold text-red-700 dark:text-red-400">
          Expired
        </span>
        <span className="text-xs font-semibold text-red-600 dark:text-red-400">
          Copy stopped
        </span>
      </div>
    );
  }

  if (row.brokerConnected) {
    return (
      <div className="flex flex-col items-center gap-0.5 text-center">
        <span className="text-sm font-bold text-emerald-700 dark:text-emerald-300">
          Connected
        </span>
        {row.balanceDisplay ? (
          <span className="text-sm font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
            {row.balanceDisplay}
          </span>
        ) : null}
        {maxDd != null ? (
          <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
            Max DD {maxDd}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-0.5 text-center">
      <span className="text-sm font-bold text-navy-800 dark:text-white">
        Disconnected
      </span>
      {maxDd != null ? (
        <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
          Max DD {maxDd}
        </span>
      ) : null}
    </div>
  );
}

export default function DashboardAccountRiskControls({
  accounts,
  perfAccounts = [],
  effectiveClientId,
}) {
  const [savedBySlot, setSavedBySlot] = useState({});
  const [draftBySlot, setDraftBySlot] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingSlot, setSavingSlot] = useState(null);
  const [error, setError] = useState("");
  const [rowMessage, setRowMessage] = useState({});
  const {
    locks: riskLocks,
    loading: locksLoading,
    error: locksError,
  } = useFirstMonthRiskLocks(effectiveClientId);

  const applyDraftsFromServer = useCallback(
    (server) => {
      const drafts = {};
      for (const row of accounts) {
        const slot = Number(row.slot);
        const locked = isSlotRiskLocked(riskLocks, slot);
        drafts[slot] = riskRowFromServer(server?.[slot], { locked });
      }
      setDraftBySlot(drafts);
    },
    [accounts, riskLocks],
  );

  const loadRisk = useCallback(async () => {
    if (!effectiveClientId) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(apiUrl("/api/client/copy-slave-risk-settings"), {
        credentials: "include",
        cache: "no-store",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(
          clientSafeMessage(data.message, "Could not load risk settings."),
        );
      }
      const server = data.bySlot && typeof data.bySlot === "object" ? data.bySlot : {};
      setSavedBySlot(server);
      setRowMessage({});
      applyDraftsFromServer(server);
    } catch (e) {
      setError(clientSafeMessage(e?.message, "Could not load risk settings."));
    } finally {
      setLoading(false);
    }
  }, [effectiveClientId, accounts.length, applyDraftsFromServer]);

  useEffect(() => {
    void loadRisk();
  }, [loadRisk]);

  useEffect(() => {
    if (locksLoading) return;
    applyDraftsFromServer(savedBySlot);
  }, [riskLocks, locksLoading, applyDraftsFromServer, savedBySlot]);

  const updateDraft = (slot, patch) => {
    if (isSlotRiskLocked(riskLocks, slot)) return;
    setDraftBySlot((prev) => {
      const base = prev[slot] || riskRowFromServer(savedBySlot[slot]);
      return { ...prev, [slot]: { ...base, ...patch } };
    });
    setRowMessage((m) => {
      const next = { ...m };
      delete next[slot];
      return next;
    });
  };

  const saveRow = async (row) => {
    if (!row.linkedToCopier && !row.tradeCopierAccountId) {
      setError("Connect this account in Subscription before saving risk.");
      return;
    }
    const slot = Number(row.slot);
    const locked = isSlotRiskLocked(riskLocks, slot);
    if (locked) {
      setRowMessage((m) => ({
        ...m,
        [slot]: FIRST_MONTH_RISK_NOTICE,
      }));
      return;
    }
    const saved = riskRowFromServer(savedBySlot[slot]);
    const draft = draftBySlot[slot] || saved;

    const v = String(draft.riskValue).trim();
    if (!v || Number.isNaN(Number(v)) || Number(v) <= 0) {
      setRowMessage((m) => ({
        ...m,
        [slot]: "Risk value must be a positive number.",
      }));
      return;
    }

    setSavingSlot(slot);
    setError("");
    setRowMessage((m) => {
      const next = { ...m };
      delete next[slot];
      return next;
    });

    try {
      await pushCopySettingsToDuplikium({
        slot,
        riskManagement: draft.riskManagement,
        riskValue: v,
        copyOn: saved.copyOn,
      });
      const confirmed = { ...draft, riskValue: v };
      setSavedBySlot((s) => ({ ...s, [slot]: confirmed }));
      setDraftBySlot((d) => ({ ...d, [slot]: confirmed }));
      setRowMessage((m) => ({ ...m, [slot]: "Saved." }));
    } catch (e) {
      setRowMessage((m) => ({
        ...m,
        [slot]: clientSafeMessage(e?.message, "Save failed."),
      }));
    } finally {
      setSavingSlot(null);
    }
  };

  const RISK_TABLE_COLS = 8;

  if (!accounts.length) return null;

  const controlsLoading = loading || locksLoading;

  return (
    <div className="mt-4 space-y-3">
      {error ? (
        <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
      ) : null}
      {locksError ? (
        <p className="text-xs text-red-600 dark:text-red-400">{locksError}</p>
      ) : null}
      {controlsLoading ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Loading risk settings…
        </p>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-white/10">
        <table className="w-full min-w-[640px] border-collapse bg-white dark:bg-navy-900/40">
          <thead>
            <tr>
              <th className={TH} rowSpan={2}>
                Trading account ID
              </th>
              <th className={TH} rowSpan={2}>
                Type
              </th>
              <th className={TH} rowSpan={2}>
                Account number
              </th>
              <th className={TH} rowSpan={2}>
                Server
              </th>
              <th className={TH} rowSpan={2}>
                Status
              </th>
              <th
                className={`${TH} border-l border-gray-200 text-center dark:border-white/10`}
                colSpan={3}
              >
                Risk management
              </th>
            </tr>
            <tr>
              <th
                className={`${TH} border-l border-gray-200 dark:border-white/10`}
              >
                Risk factor
              </th>
              <th className={TH}>Risk value</th>
              <th className={TH} />
            </tr>
          </thead>
          <tbody>
            {accounts.map((row) => {
              const slot = Number(row.slot);
              const locked = isSlotRiskLocked(riskLocks, slot);
              const saved = riskRowFromServer(savedBySlot[slot], { locked });
              const draft = draftBySlot[slot] || saved;
              const dirty = !locked && !riskRowsEqual(draft, saved);
              const busy = savingSlot === slot;
              const canEdit =
                !controlsLoading &&
                !locked &&
                !row.subscriptionExpired &&
                (row.linkedToCopier || row.tradeCopierAccountId);
              const perf = perfAccounts.find((p) => p.slot === row.slot);
              const platformLabel =
                row.accountType === "MT4"
                  ? "MetaTrader 4"
                  : row.accountType === "MT5"
                    ? "MetaTrader 5"
                    : row.accountType || "—";

              return (
                <>
                  <tr
                    key={row.id}
                    className="bg-gray-50/50 dark:bg-navy-900/20"
                  >
                    <td className={TD}>
                      <span className="font-mono text-sm font-bold text-brand-700 dark:text-brand-200">
                        {row.copierDisplayName || `S${slot}`}
                      </span>
                    </td>
                    <td className={TD}>{platformLabel}</td>
                    <td className={`${TD} tabular-nums`}>
                      {row.accountNumber || "—"}
                    </td>
                    <td className={TD}>{row.server || "—"}</td>
                    <td className={TD}>
                      <StatusCell row={row} perf={perf} />
                    </td>
                    <td
                      className={`${TD} border-l border-gray-100 dark:border-white/5`}
                    >
                      <select
                        className={SELECT_CLASS}
                        value={locked ? RISK_UI_FIXED_LOT : draft.riskManagement}
                        disabled={!canEdit || busy || locked}
                        aria-label="Risk factor"
                        aria-disabled={locked}
                        onChange={(e) => {
                          if (locked) return;
                          const riskManagement = e.target.value;
                          updateDraft(slot, {
                            riskManagement,
                            riskValue: defaultRiskValueForType(riskManagement),
                          });
                        }}
                      >
                        {RISK_FACTOR_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className={TD}>
                      <input
                        type="text"
                        inputMode="decimal"
                        className={INPUT_CLASS}
                        value={locked ? "0.01" : draft.riskValue}
                        readOnly={locked}
                        disabled={!canEdit || busy || locked}
                        aria-label="Risk value"
                        onChange={(e) => {
                          if (locked) return;
                          updateDraft(slot, { riskValue: e.target.value });
                        }}
                      />
                    </td>
                    <td className={TD}>
                      <div className="flex flex-col items-center gap-1">
                        <button
                          type="button"
                          disabled={!canEdit || busy || !dirty}
                          onClick={() => void saveRow(row)}
                          className="rounded-lg bg-brand-500 px-4 py-1.5 text-sm font-bold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-brand-400 dark:hover:bg-brand-300"
                        >
                          {busy ? "Saving…" : "Save"}
                        </button>
                        {!locked && rowMessage[slot] ? (
                          <p
                            className={`text-xs font-semibold ${
                              rowMessage[slot] === "Saved."
                                ? "text-emerald-600 dark:text-emerald-400"
                                : "text-red-600 dark:text-red-400"
                            }`}
                          >
                            {rowMessage[slot]}
                          </p>
                        ) : null}
                        {!locked && dirty && canEdit ? (
                          <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                            Unsaved changes
                          </p>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                  {locked ? (
                    <tr
                      key={`${row.id}-first-month-notice`}
                      className="bg-amber-50/80 dark:bg-amber-950/25"
                    >
                      <td
                        colSpan={RISK_TABLE_COLS}
                        className="border-b border-amber-200/80 px-3 py-2.5 dark:border-amber-500/30"
                      >
                        <p
                          className="text-sm font-semibold leading-snug text-amber-950 dark:text-amber-100"
                          role="status"
                        >
                          {FIRST_MONTH_RISK_NOTICE} Risk stays{" "}
                          <strong>Fixed lot 0.01</strong> for the first 30 days
                          after activation.
                        </p>
                      </td>
                    </tr>
                  ) : null}
                </>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-gray-500 dark:text-gray-500">
        Change risk factor or value, then click Save. Settings apply to copy
        trading for that slot.
      </p>
    </div>
  );
}
